import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine-ext-loader';
import { BlockViewExtension, FlavourExtension } from '@blocksuite/std';
import { literal } from 'lit/static-html.js';

import { AppBlockInteraction, registerAppScreenViewing } from './app/index.js';
import { AutomationBlockInteraction } from './automation/index.js';
import {
  BrushHeadingBlockInteraction,
  brushHeadingToolbarExtension,
} from './brush-heading/index.js';
import {
  EdgelessClipboardAutomationConfig,
  EdgelessClipboardBrushHeadingConfig,
  EdgelessClipboardElsterConfig,
  EdgelessClipboardGhostConfig,
  EdgelessClipboardStickyNoteConfig,
  EdgelessClipboardTutorialCardConfig,
} from './edgeless-clipboard-configs.js';
import { effects } from './effects.js';
import { ElsterBlockInteraction } from './elster/index.js';
import { GhostBlockInteraction } from './ghost/index.js';
import {
  AppBlockSchema,
  AutomationBlockSchema,
  BrushHeadingBlockSchema,
  ElsterBlockSchema,
  GhostBlockSchema,
  StickyNoteBlockSchema,
  TrashcanBlockSchema,
  TutorialCardBlockSchema,
  WorkflowStepBlockSchema,
} from './schemas/index.js';
import {
  StickyNoteBlockInteraction,
  stickyNoteToolbarExtension,
} from './sticky-note/index.js';
import { AutomationConnectorTool } from './tools/index.js';
import { TutorialCardBlockInteraction } from './tutorial-card/index.js';
import { WorkflowStepBlockInteraction } from './workflow-step/index.js';

// Static literal tags for Lit components
const appCanvasTag = literal`affine-app-canvas`;
const automationDocTag = literal`affine-automation`;
const automationCanvasTag = literal`affine-automation-canvas`;
const elsterDocTag = literal`affine-elster`;
const elsterCanvasTag = literal`affine-elster-canvas`;
const ghostCanvasTag = literal`affine-ghost-canvas`;
const tutorialCardCanvasTag = literal`affine-tutorial-card-canvas`;
const stickyNoteCanvasTag = literal`affine-sticky-note-canvas`;
const brushHeadingCanvasTag = literal`affine-brush-heading-canvas`;
const workflowStepCanvasTag = literal`affine-workflow-step-canvas`;

/**
 * StreamLain View Extension
 *
 * Registers all StreamLain block views with the BlockSuite view layer.
 * Handles both Doc and Canvas (Edgeless) rendering modes.
 */
export class StreamLainViewExtension extends ViewExtensionProvider {
  override name = 'affine-streamlain-block';

  override effect() {
    super.effect();
    effects();
  }

  override setup(context: ViewExtensionContext) {
    super.setup(context);

    // Automation Block View
    const automationFlavour = AutomationBlockSchema.model.flavour;
    context.register([
      FlavourExtension(automationFlavour),
      BlockViewExtension(automationFlavour, model => {
        // Canvas vs Doc View based on parent
        return model.parent?.flavour === 'affine:note'
          ? automationDocTag
          : automationCanvasTag;
      }),
    ]);

    // ELSTER Block View (sl-yphr)
    const elsterFlavour = ElsterBlockSchema.model.flavour;
    context.register([
      FlavourExtension(elsterFlavour),
      BlockViewExtension(elsterFlavour, model => {
        return model.parent?.flavour === 'affine:note'
          ? elsterDocTag
          : elsterCanvasTag;
      }),
    ]);

    // Register interaction, clipboard, and tool extensions for edgeless mode
    const isEdgeless = this.isEdgeless(context.scope);
    if (isEdgeless) {
      context.register(AutomationBlockInteraction);
      context.register(ElsterBlockInteraction);
      context.register(AutomationConnectorTool);
      // Clipboard configs for copy/paste on canvas
      context.register(EdgelessClipboardAutomationConfig);
      context.register(EdgelessClipboardElsterConfig);
    }

    // Trashcan Block - No visual rendering, just data storage
    const trashcanFlavour = TrashcanBlockSchema.model.flavour;
    context.register([
      FlavourExtension(trashcanFlavour),
      // No BlockViewExtension - trashcan is invisible
    ]);

    // Ghost Block View (E012-T013) - Canvas only
    const ghostFlavour = GhostBlockSchema.model.flavour;
    context.register([
      FlavourExtension(ghostFlavour),
      BlockViewExtension(ghostFlavour, () => {
        // Ghosts are canvas-only (no Doc view)
        return ghostCanvasTag;
      }),
    ]);

    // Register ghost interaction and clipboard extension for edgeless mode
    if (isEdgeless) {
      context.register(GhostBlockInteraction);
      context.register(EdgelessClipboardGhostConfig);
    }

    // Tutorial Card Block View (sl-728o.7.1) - Canvas only
    const tutorialCardFlavour = TutorialCardBlockSchema.model.flavour;
    context.register([
      FlavourExtension(tutorialCardFlavour),
      BlockViewExtension(tutorialCardFlavour, () => {
        return tutorialCardCanvasTag;
      }),
    ]);

    if (isEdgeless) {
      context.register(TutorialCardBlockInteraction);
      context.register(EdgelessClipboardTutorialCardConfig);
    }

    const stickyNoteFlavour = StickyNoteBlockSchema.model.flavour;
    context.register([
      FlavourExtension(stickyNoteFlavour),
      BlockViewExtension(stickyNoteFlavour, () => stickyNoteCanvasTag),
    ]);
    if (isEdgeless) {
      context.register(StickyNoteBlockInteraction);
      context.register(EdgelessClipboardStickyNoteConfig);
      context.register(stickyNoteToolbarExtension);
    }

    // App Block View - Canvas only
    const appFlavour = AppBlockSchema.model.flavour;
    context.register([
      FlavourExtension(appFlavour),
      BlockViewExtension(appFlavour, () => appCanvasTag),
    ]);
    if (isEdgeless) {
      context.register(AppBlockInteraction);
    }
    registerAppScreenViewing(context, isEdgeless);

    // Workflow Step Block View - Canvas only
    const workflowStepFlavour = WorkflowStepBlockSchema.model.flavour;
    context.register([
      FlavourExtension(workflowStepFlavour),
      BlockViewExtension(workflowStepFlavour, () => workflowStepCanvasTag),
    ]);
    if (isEdgeless) {
      context.register(WorkflowStepBlockInteraction);
    }

    const brushHeadingFlavour = BrushHeadingBlockSchema.model.flavour;
    context.register([
      FlavourExtension(brushHeadingFlavour),
      BlockViewExtension(brushHeadingFlavour, () => brushHeadingCanvasTag),
    ]);
    if (isEdgeless) {
      context.register(BrushHeadingBlockInteraction);
      context.register(EdgelessClipboardBrushHeadingConfig);
      context.register(brushHeadingToolbarExtension);
    }
  }
}
